import React, { useState, useEffect, useRef } from 'react';
import { useAppContext } from '../../context/AppContext';
import { useWorkspaceContext } from '../../pages/ChatPage/providers/WorkspaceProvider';
import { Building, Briefcase, User, FileText, Check, Loader2, AlertCircle } from 'lucide-react';

export default function CoverLetterEditor() {
  const {
    authToken,
    activeCoverLetter,
    setActiveCoverLetter,
    onCoverLetterUpdate,
    setAutosaveStatus,
    resumeData,
  } = useAppContext();

  const { theme } = useWorkspaceContext();
  const isDark = theme === 'dark';

  // Form local state
  const [senderName, setSenderName] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [senderPhone, setSenderPhone] = useState('');
  const [company, setCompany] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [recipient, setRecipient] = useState('');
  const [content, setContent] = useState('');

  // Autosave status state
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'

  useEffect(() => {
    if (setAutosaveStatus) {
      setAutosaveStatus(saveStatus);
    }
  }, [saveStatus, setAutosaveStatus]);

  const saveTimeoutRef = useRef(null);
  const isDirtyRef = useRef(false);
  const prevLetterIdRef = useRef(undefined);

  // Sync state from activeCoverLetter — on document switch (always), and on
  // any OTHER external update (e.g. the AI Coach just drafted/redrafted this
  // letter) as long as there's no unsaved local edit in flight. That second
  // case matters: without it, the editor's fields only ever refreshed when
  // you switched documents, so a fresh AI draft would update the preview
  // immediately but leave the editor's own fields showing stale/empty values
  // until you navigated away and back. Never overwrite mid-keystroke — that's
  // still guarded by isDirtyRef (cursor jump / lost input otherwise).
  useEffect(() => {
    if (!activeCoverLetter) return;
    const idChanged = prevLetterIdRef.current !== activeCoverLetter.id;
    prevLetterIdRef.current = activeCoverLetter.id;

    if (!idChanged && isDirtyRef.current) return;

    const sender = activeCoverLetter.sender || {};
    // Pre-fill from your resume if this letter has no sender info of its own
    // yet — you can still override any of it here, it just isn't blank.
    setSenderName(sender.name || resumeData?.name || '');
    setSenderEmail(sender.email || resumeData?.email || '');
    setSenderPhone(sender.phone || resumeData?.phone || '');
    setCompany(activeCoverLetter.company || '');
    setJobTitle(activeCoverLetter.job_title || '');
    setRecipient(activeCoverLetter.recipient || '');
    setContent(activeCoverLetter.content || '');
    if (idChanged) {
      setSaveStatus('idle');
      isDirtyRef.current = false;
    }
  }, [activeCoverLetter, resumeData]);

  const latestSenderNameRef = useRef(senderName);
  const latestSenderEmailRef = useRef(senderEmail);
  const latestSenderPhoneRef = useRef(senderPhone);
  const latestCompanyRef = useRef(company);
  const latestJobTitleRef = useRef(jobTitle);
  const latestRecipientRef = useRef(recipient);
  const latestContentRef = useRef(content);
  const latestActiveCoverLetterRef = useRef(activeCoverLetter);
  const latestAuthTokenRef = useRef(authToken);
  const onCoverLetterUpdateRef = useRef(onCoverLetterUpdate);

  // Sync refs on state changes
  useEffect(() => {
    latestSenderNameRef.current = senderName;
    latestSenderEmailRef.current = senderEmail;
    latestSenderPhoneRef.current = senderPhone;
    latestCompanyRef.current = company;
    latestJobTitleRef.current = jobTitle;
    latestRecipientRef.current = recipient;
    latestContentRef.current = content;
    latestActiveCoverLetterRef.current = activeCoverLetter;
    latestAuthTokenRef.current = authToken;
    onCoverLetterUpdateRef.current = onCoverLetterUpdate;
  }, [senderName, senderEmail, senderPhone, company, jobTitle, recipient, content, activeCoverLetter, authToken, onCoverLetterUpdate]);

  // Debounced autosave handler
  const triggerSave = (updatedFields) => {
    if (!activeCoverLetter) return;
    setSaveStatus('dirty');
    isDirtyRef.current = true;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      setSaveStatus('saving');
      try {
        const bodyData = {
          sender: {
            name: updatedFields.senderName !== undefined ? updatedFields.senderName : senderName,
            email: updatedFields.senderEmail !== undefined ? updatedFields.senderEmail : senderEmail,
            phone: updatedFields.senderPhone !== undefined ? updatedFields.senderPhone : senderPhone,
          },
          company: updatedFields.company !== undefined ? updatedFields.company : company,
          job_title: updatedFields.jobTitle !== undefined ? updatedFields.jobTitle : jobTitle,
          recipient: updatedFields.recipient !== undefined ? updatedFields.recipient : recipient,
          content: updatedFields.content !== undefined ? updatedFields.content : content,
        };

        const res = await fetch(`/api/cover-letter/${activeCoverLetter.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify(bodyData),
        });

        if (res.ok) {
          const data = await res.json();
          // Update parent AppContext state to trigger live preview re-renders
          onCoverLetterUpdate(data);
          setSaveStatus('saved');
          isDirtyRef.current = false;
        } else {
          setSaveStatus('error');
        }
      } catch (err) {
        console.error('Error saving cover letter:', err);
        setSaveStatus('error');
      }
    }, 800);
  };

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      // Emergency save on unmount if there are unsaved changes
      if (isDirtyRef.current && latestActiveCoverLetterRef.current && latestAuthTokenRef.current) {
        const bodyData = {
          sender: {
            name: latestSenderNameRef.current,
            email: latestSenderEmailRef.current,
            phone: latestSenderPhoneRef.current,
          },
          company: latestCompanyRef.current,
          job_title: latestJobTitleRef.current,
          recipient: latestRecipientRef.current,
          content: latestContentRef.current,
        };
        
        console.log(`[CoverLetterEditor] Unmounting with unsaved changes. Performing emergency save for CL ${latestActiveCoverLetterRef.current.id}...`);
        
        fetch(`/api/cover-letter/${latestActiveCoverLetterRef.current.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${latestAuthTokenRef.current}`,
          },
          body: JSON.stringify(bodyData),
          keepalive: true
        }).then(async (res) => {
          if (res.ok) {
            const data = await res.json();
            if (onCoverLetterUpdateRef.current) {
              onCoverLetterUpdateRef.current(data);
            }
          }
        }).catch(err => {
          console.error('[CoverLetterEditor] Failed to perform emergency save on unmount:', err);
        });
      }
    };
  }, []);

  const SENDER_FIELDS = { senderName: 'name', senderEmail: 'email', senderPhone: 'phone' };

  const handleFieldChange = (fieldName, val) => {
    // Optimistic update: immediately reflect change in AppContext so the right-side
    // preview iframe re-renders without waiting for the debounced API save.
    if (activeCoverLetter && setActiveCoverLetter) {
      if (SENDER_FIELDS[fieldName]) {
        const senderKey = SENDER_FIELDS[fieldName];
        setActiveCoverLetter((prev) => prev
          ? { ...prev, sender: { ...(prev.sender || {}), [senderKey]: val } }
          : prev);
      } else {
        const fieldMap = {
          company: 'company',
          jobTitle: 'job_title',
          recipient: 'recipient',
          content: 'content',
        };
        const apiKey = fieldMap[fieldName];
        if (apiKey) {
          setActiveCoverLetter((prev) => prev ? { ...prev, [apiKey]: val } : prev);
        }
      }
    }

    if (fieldName === 'senderName') {
      setSenderName(val);
      triggerSave({ senderName: val });
    } else if (fieldName === 'senderEmail') {
      setSenderEmail(val);
      triggerSave({ senderEmail: val });
    } else if (fieldName === 'senderPhone') {
      setSenderPhone(val);
      triggerSave({ senderPhone: val });
    } else if (fieldName === 'company') {
      setCompany(val);
      triggerSave({ company: val });
    } else if (fieldName === 'jobTitle') {
      setJobTitle(val);
      triggerSave({ jobTitle: val });
    } else if (fieldName === 'recipient') {
      setRecipient(val);
      triggerSave({ recipient: val });
    } else if (fieldName === 'content') {
      setContent(val);
      triggerSave({ content: val });
    }
  };

  if (!activeCoverLetter) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '24px', textAlign: 'center' }}>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>No active cover letter to edit.</p>
      </div>
    );
  }

  // Styles defined dynamically based on theme
  const containerBg = isDark ? '#000000' : '#ffffff';
  const labelColor = isDark ? '#a1a1aa' : '#4b5563';
  const inputBg = isDark ? '#18181b' : '#f9fafb';
  const inputBorder = isDark ? '#27272a' : '#e5e7eb';
  const inputBorderFocus = 'var(--brand)';
  const textColor = isDark ? '#f4f4f5' : '#111827';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: containerBg }} className="font-sans">
      {/* Header bar */}
      <div 
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          padding: '14px 20px', 
          borderBottom: '1px solid var(--divider)', 
          flexShrink: 0 
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '15px' }}>✉️</span>
          <span style={{ fontWeight: 700, fontSize: '14px', color: textColor }}>Cover Letter Editor</span>
        </div>

        {/* Autosave Indicator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {saveStatus === 'dirty' && (
            <>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--status-warning)' }} />
              <span style={{ fontSize: '11px', color: isDark ? '#a1a1aa' : '#6b7280' }}>Unsaved Changes</span>
            </>
          )}
          {saveStatus === 'saving' && (
            <>
              <Loader2 size={12} className="animate-spin" style={{ color: 'var(--brand)' }} />
              <span style={{ fontSize: '11px', color: 'var(--brand)' }}>Saving...</span>
            </>
          )}
          {saveStatus === 'saved' && (
            <>
              <Check size={12} style={{ color: 'var(--status-success)' }} />
              <span style={{ fontSize: '11px', color: 'var(--status-success)' }}>Saved</span>
            </>
          )}
          {saveStatus === 'error' && (
            <>
              <AlertCircle size={12} style={{ color: 'var(--status-danger)' }} />
              <span style={{ fontSize: '11px', color: 'var(--status-danger)', fontWeight: 600 }}>Save Failed</span>
            </>
          )}
          {saveStatus === 'idle' && (
            <>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--status-success)' }} />
              <span style={{ fontSize: '11px', color: isDark ? '#71717a' : '#9ca3af' }}>Saved</span>
            </>
          )}
        </div>
      </div>

      {/* Editor Fields form */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }} className="space-y-5 tg-scrollbar">
        {/* Your Details — name/email/phone shown in the letterhead. Pre-filled
            from your resume but editable/overridable per letter. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', paddingBottom: '14px', borderBottom: `1px solid ${inputBorder}` }}>
          <label style={{ fontSize: '11px', fontWeight: 600, color: labelColor, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <User size={12} /> Your Details
          </label>
          <input
            type="text"
            value={senderName}
            onChange={(e) => handleFieldChange('senderName', e.target.value)}
            placeholder="Your full name"
            style={{
              width: '100%', boxSizing: 'border-box', background: inputBg,
              border: `1px solid ${inputBorder}`, borderRadius: '8px',
              padding: '10px 12px', fontSize: '13px', color: textColor,
              outline: 'none', transition: 'border-color 0.15s ease',
            }}
            onFocus={(e) => (e.target.style.borderColor = inputBorderFocus)}
            onBlur={(e) => (e.target.style.borderColor = inputBorder)}
          />
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              value={senderEmail}
              onChange={(e) => handleFieldChange('senderEmail', e.target.value)}
              placeholder="Email"
              style={{
                flex: 1, boxSizing: 'border-box', background: inputBg,
                border: `1px solid ${inputBorder}`, borderRadius: '8px',
                padding: '10px 12px', fontSize: '13px', color: textColor,
                outline: 'none', transition: 'border-color 0.15s ease',
              }}
              onFocus={(e) => (e.target.style.borderColor = inputBorderFocus)}
              onBlur={(e) => (e.target.style.borderColor = inputBorder)}
            />
            <input
              type="text"
              value={senderPhone}
              onChange={(e) => handleFieldChange('senderPhone', e.target.value)}
              placeholder="Phone"
              style={{
                flex: 1, boxSizing: 'border-box', background: inputBg,
                border: `1px solid ${inputBorder}`, borderRadius: '8px',
                padding: '10px 12px', fontSize: '13px', color: textColor,
                outline: 'none', transition: 'border-color 0.15s ease',
              }}
              onFocus={(e) => (e.target.style.borderColor = inputBorderFocus)}
              onBlur={(e) => (e.target.style.borderColor = inputBorder)}
            />
          </div>
        </div>

        {/* Company input */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontSize: '11px', fontWeight: 600, color: labelColor, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Building size={12} /> Target Company
          </label>
          <input
            type="text"
            value={company}
            onChange={(e) => handleFieldChange('company', e.target.value)}
            placeholder="e.g. Google"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              background: inputBg,
              border: `1px solid ${inputBorder}`,
              borderRadius: '8px',
              padding: '10px 12px',
              fontSize: '13px',
              color: textColor,
              outline: 'none',
              transition: 'border-color 0.15s ease',
            }}
            onFocus={(e) => (e.target.style.borderColor = inputBorderFocus)}
            onBlur={(e) => (e.target.style.borderColor = inputBorder)}
          />
        </div>

        {/* Job Title input */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontSize: '11px', fontWeight: 600, color: labelColor, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Briefcase size={12} /> Target Role
          </label>
          <input
            type="text"
            value={jobTitle}
            onChange={(e) => handleFieldChange('jobTitle', e.target.value)}
            placeholder="e.g. Senior Software Engineer"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              background: inputBg,
              border: `1px solid ${inputBorder}`,
              borderRadius: '8px',
              padding: '10px 12px',
              fontSize: '13px',
              color: textColor,
              outline: 'none',
              transition: 'border-color 0.15s ease',
            }}
            onFocus={(e) => (e.target.style.borderColor = inputBorderFocus)}
            onBlur={(e) => (e.target.style.borderColor = inputBorder)}
          />
        </div>

        {/* Recipient input */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontSize: '11px', fontWeight: 600, color: labelColor, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <User size={12} /> Recipient Name / Hiring Manager
          </label>
          <input
            type="text"
            value={recipient}
            onChange={(e) => handleFieldChange('recipient', e.target.value)}
            placeholder="e.g. Hiring Manager or John Doe"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              background: inputBg,
              border: `1px solid ${inputBorder}`,
              borderRadius: '8px',
              padding: '10px 12px',
              fontSize: '13px',
              color: textColor,
              outline: 'none',
              transition: 'border-color 0.15s ease',
            }}
            onFocus={(e) => (e.target.style.borderColor = inputBorderFocus)}
            onBlur={(e) => (e.target.style.borderColor = inputBorder)}
          />
        </div>

        {/* Body content textarea */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', height: 'calc(100% - 250px)', minHeight: '300px' }}>
          <label style={{ fontSize: '11px', fontWeight: 600, color: labelColor, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FileText size={12} /> Letter Content
          </label>
          <textarea
            value={content}
            onChange={(e) => handleFieldChange('content', e.target.value)}
            placeholder="Write your cover letter here..."
            style={{
              width: '100%',
              height: '100%',
              boxSizing: 'border-box',
              background: inputBg,
              border: `1px solid ${inputBorder}`,
              borderRadius: '8px',
              padding: '14px',
              fontSize: '13.5px',
              color: textColor,
              outline: 'none',
              resize: 'none',
              lineHeight: 1.6,
              fontFamily: 'inherit',
              transition: 'border-color 0.15s ease',
            }}
            onFocus={(e) => (e.target.style.borderColor = inputBorderFocus)}
            onBlur={(e) => (e.target.style.borderColor = inputBorder)}
          />
        </div>
      </div>
    </div>
  );
}
